import React, { useState, useEffect } from 'react';
import {
  Anchor, Button, Center, Group, MultiSelect, Paper, PasswordInput,
  Stack, Text, TextInput, Title,
} from '@mantine/core';

const REGIO_PREFIX = '__regio__';

function RegisterPage({ onRegisterSuccess, onShowLogin }) {
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [voornaam, setVoornaam] = useState('');
  const [achternaam, setAchternaam] = useState('');
  const [regios, setRegios] = useState([]);            // [{regio, gemeenten:[{code,naam}]}]
  const [gekozenGemeenten, setGekozenGemeenten] = useState([]);  // gemeentecodes
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch('/api/gemeenten-per-regio')
      .then(r => r.json())
      .then(data => { if (data.success) setRegios(data.regios || []); })
      .catch(() => setRegios([]));
  }, []);

  const gemeenteData = regios.map(r => ({
    group: r.regio,
    items: [
      { value: REGIO_PREFIX + r.regio, label: `➕ Hele regio ${r.regio}` },
      ...r.gemeenten.map(g => ({ value: g.code, label: g.naam })),
    ],
  }));

  const kiesGemeenten = (waarden) => {
    const codes = new Set();
    waarden.forEach(v => {
      if (v.startsWith(REGIO_PREFIX)) {
        const regio = v.slice(REGIO_PREFIX.length);
        (regios.find(r => r.regio === regio)?.gemeenten || []).forEach(g => codes.add(g.code));
      } else codes.add(v);
    });
    setGekozenGemeenten([...codes]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gekozenGemeenten.length) {
      setError('Kies minstens één regio of gemeente.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, voornaam, achternaam, gemeenten: gekozenGemeenten }),
      });
      const data = await response.json();
      if (data.success) onRegisterSuccess();
      else setError(data.error || 'Registratie mislukt');
    } catch (err) {
      setError('Netwerkfout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Center
      style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #e8f8ef 0%, #d0efdd 100%)' }}
    >
      <Paper component="form" p="xl" shadow="md" radius="md" withBorder
        style={{ width: 420, maxWidth: '92vw' }} onSubmit={handleSubmit}>
        <Stack gap="md">
          <Title order={2} c="groen.7" ta="center">Registreren</Title>
          <Group grow gap="xs">
            <TextInput label="Voornaam" placeholder="Voornaam" value={voornaam} autoFocus
              onChange={e => setVoornaam(e.currentTarget.value)} autoComplete="given-name" required />
            <TextInput label="Achternaam" placeholder="Achternaam" value={achternaam}
              onChange={e => setAchternaam(e.currentTarget.value)} autoComplete="family-name" required />
          </Group>
          <TextInput label="E-mail" placeholder="E-mailadres" type="email" value={email}
            description="Hiermee log je in"
            onChange={e => setEmail(e.currentTarget.value)} autoComplete="email" required />
          <PasswordInput label="Wachtwoord" placeholder="Wachtwoord" value={password}
            onChange={e => setPassword(e.currentTarget.value)} autoComplete="new-password" required />
          <MultiSelect label="Regio / gemeenten (waar je werkzaam bent)"
            description="Kies een hele regio of losse gemeenten" required
            placeholder={gekozenGemeenten.length ? '' : 'Kies regio/gemeenten...'}
            data={gemeenteData} value={gekozenGemeenten} onChange={kiesGemeenten}
            searchable clearable hidePickedOptions maxDropdownHeight={260} />
          {error && <Text c="red" size="sm">{error}</Text>}
          <Button type="submit" loading={loading} fullWidth>Registreren</Button>
          <Text size="sm" ta="center" c="dimmed">
            Al een account?{' '}
            <Anchor component="button" type="button" onClick={onShowLogin}>Inloggen</Anchor>
          </Text>
        </Stack>
      </Paper>
    </Center>
  );
}

export default RegisterPage;
